import { dispatchAction } from '@ardoq/rxbeach';
import { ViewIds } from '@ardoq/api-types';
import { updateViewSettings } from '@ardoq/view-settings';
import { isEqual } from 'lodash';
import type { BlocksArrangement } from 'tabview/assistantBlocks/arrangementRecipe';
import { defineAiControlledCommand } from 'aiControlledCommands/defineAiControlledCommand';
import type { AiControlledCommand } from 'aiControlledCommands/types';
import { INTERACTIVE_BLOCKS_DESCRIPTION } from './descriptions';
import { toGroupLinks } from 'tabview/assistantBlocks/groupLinks';
import { type BlocksContextArgs, buildBlocksContext } from './context';
import {
  type BlocksCommandContext,
  isBlocksCommandContext,
  LAYOUT_COMMAND_METADATA,
  type ResolvedBlocksLayout,
  validateLayout,
} from './layoutCommand';

type InteractiveBlocksLayoutSettings = {
  arrangements: BlocksArrangement[];
  pendingArrangementContainerIds: (string | null)[];
};

/**
 * The arrangements to save, with the groups the recipe names marked as pending, so the view lays
 * them out afresh once and keeps later hand edits.
 */
export const toInteractiveBlocksLayoutSettings = ({
  recipe,
  expectedArrangements,
}: Pick<
  ResolvedBlocksLayout,
  'recipe' | 'expectedArrangements'
>): InteractiveBlocksLayoutSettings => ({
  arrangements: expectedArrangements,
  pendingArrangementContainerIds: recipe.arrangements.map(
    ({ containerId }) => containerId
  ),
});

export const executeInteractiveBlocksLayout = (
  layout: Pick<ResolvedBlocksLayout, 'recipe' | 'expectedArrangements'>
): void =>
  dispatchAction(
    updateViewSettings({
      viewId: ViewIds.BLOCKS,
      settings: toInteractiveBlocksLayoutSettings(layout),
      persistent: true,
    })
  );

export const interactiveBlocksLayoutCommandDefinition =
  defineAiControlledCommand<ResolvedBlocksLayout, BlocksCommandContext>({
    ...LAYOUT_COMMAND_METADATA,
    description: INTERACTIVE_BLOCKS_DESCRIPTION,
    isContext: isBlocksCommandContext,
    validate: (rawPayload, _context, state) =>
      validateLayout({
        viewId: ViewIds.BLOCKS,
        rawPayload,
        state,
        findGroupLinks: toGroupLinks,
      }),
    execute: executeInteractiveBlocksLayout,
    describeApplied: ({ report }) => report,
    isApplied: ({ expectedArrangements }, context) =>
      isEqual(context.arrangements, expectedArrangements),
  });

type BuildInteractiveBlocksLayoutCommandArgs = BlocksContextArgs & {
  arrangements: BlocksArrangement[];
};

export const buildInteractiveBlocksLayoutCommand = ({
  arrangements,
  ...contextArgs
}: BuildInteractiveBlocksLayoutCommandArgs): AiControlledCommand<
  typeof interactiveBlocksLayoutCommandDefinition
> => ({
  ...interactiveBlocksLayoutCommandDefinition,
  context: {
    ...buildBlocksContext({ ...contextArgs, arrangements }),
    title: null,
    arrangements,
  },
});
